import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Formik } from "formik";
import * as Yup from "yup";
import _groupBy from "lodash/groupBy";
import { Link, useHistory, useParams } from "react-router-dom";

import Layout from "../../../../../components/Layout";
import Container from "../../../../../components/Container";
import Flex from "../../../../../components/Flex";

import Content from "../../../../../components/Content";
import Button from "../../../../../components/Button";
import ButtonList from "../../../../../components/ButtonList";
import Breadcrumbs from "../../../../../components/Breadcrumbs";
import { useMessageToast } from "../../../../../components/MessageProvider";
import { useFetch } from "../../../../../components/useFetch";
import { useAuthState } from "../../../../../components/AuthProvider";
import Label from "../../../../../components/Label";
import DropDownList from "../../../../../components/DropDownList";
import Spinner from "../../../../../components/Loader/Spinner";
import handleResponse from "../../../../../utils/handleFetchResponse";

import styles from "./AddEditUserAccess.module.scss";

const AddEditUserAccess = () => {
  const moment = require("moment-timezone");
  const clientConfigApiDomain = process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN;
  const userApiUrl = `${clientConfigApiDomain}/users`;
  const userRoleRpcUrl = `${clientConfigApiDomain}/rpc/user_roles`;
  const userRoleApiUrl = `${clientConfigApiDomain}/user_roles`;
  const roleApiUrl = `${clientConfigApiDomain}/roles`;
  const clientApiUrl = `${clientConfigApiDomain}/clients`;
  const clientRpcUrl = `${clientConfigApiDomain}/rpc/clients`;

  const { id, accessId } = useParams();

  const history = useHistory();
  const { user } = useAuthState();
  const toast = useMessageToast();

  const [
    {
      data: addEditUserData,
      hasLoaded: addEditHasLoaded,
      hasError: addEditHasError
    },
    addEditFetch
  ] = useFetch();

  // TODO: Move this to role configuration column in table!
  const roleAddMap = {
    "Global Admin": [
      "Global Admin",
      "Organization Admin",
      "Data Steward",
      "Read-Only"
    ],
    "Organization Admin": ["Organization Admin", "Data Steward", "Read-Only"],
    "Data Steward": ["Data Steward", "Read-Only"],
    "Read-Only": ["Read-Only"]
  };

  const [userData, setUserData] = useState([]);
  const [userAccessData, setUserRoleData] = useState([]);
  const [rolesData, setRolesData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [fetchedUserRoleData, setFetchedUserRoleData] = useState(false);
  const [userClientsFilter, setUserClientsFilter] = useState([]);

  const fetchUserRoleData = useCallback(async () => {
    window.scrollTo(0, 0);
    //const filterRoleData = role => roleAddMap[user.role].includes(role.name);
    const filterRoleData = role =>
      roleAddMap["Organization Admin"].includes(role.name);
    /*
  const roleAddMap = {
    "Global Admin": ["Global Admin", "Organization Admin", "Data Steward", "Read-Only"],
    "Organization Admin": ["Organization Admin", "Data Steward", "Read-Only"],
    "Data Steward":["Data Steward", "Read-Only"]
  };
*/

    setIsLoading(true);

    if (accessId !== undefined) {
      const response = await fetch(`${userRoleRpcUrl}/?id=${accessId}`);
      const data = await response.json();
      setUserRoleData(data.results[0]);
    }

    const userResponse = await fetch(`${userApiUrl}/${id}/`);
    const userData = await userResponse.json();
    setUserData(userData);

    const roleResponse = await fetch(`${roleApiUrl}/`);
    const rolesData = await roleResponse.json();

    const userRolesData = rolesData.results.filter(filterRoleData); // addableRoles
    setRolesData(userRolesData);

    setIsLoading(false);
    setHasLoaded(true);
  }, [
    accessId,
    id,
    roleAddMap,
    roleApiUrl,
    user.role,
    userApiUrl,
    userRoleRpcUrl
  ]);

  useEffect(() => {
    const createUserClientsOrganizationsFilter = () => {
      const filteredClients = user.clients.filter(
        client => client.organization.login_type !== "MS-Email"
      );
      const userClientMapped = filteredClients.map(client => ({
        client: client.name,
        clientId: client.id,
        organization: client.organization.name,
        id: client.organization.id
      }));
      const userClientGroupByOrg = _groupBy(userClientMapped, `organization`);
      const userClient = Object.keys(userClientGroupByOrg).map(org => ({
        text: org,
        id: id,
        children: userClientGroupByOrg[org].map(childValues => ({
          text: childValues.client,
          value: childValues.client,
          id: childValues.clientId
        }))
      }));

      setUserClientsFilter(userClient);
    };
    if (!hasLoaded && id !== undefined && !fetchedUserRoleData) {
      fetchUserRoleData();
      setFetchedUserRoleData(true);
      createUserClientsOrganizationsFilter();
    } else {
      // this enables the add screen to show up
      setHasLoaded(true);
    }
  }, [id, fetchUserRoleData, hasLoaded, user.clients, fetchedUserRoleData]);

  useEffect(() => {
    if (accessId === undefined && addEditHasLoaded) {
      if (addEditHasLoaded) {
        toast.success(`User access added successfully.`);

        history.push(`/admin/users/${id}`);
      } else if (addEditHasError) {
        toast.error(`Failed to add user access.`);
      }
    } else {
      if (addEditHasLoaded) {
        toast.success(`User access updated successfully.`);

        history.push(`/admin/users/${id}`);
      } else if (addEditHasError) {
        toast.error(`Failed to update this user access.`);
      }
    }
  }, [
    accessId,
    addEditUserData,
    addEditHasLoaded,
    addEditHasError,
    toast,
    history,
    id
  ]);

  let loadedBreadcrumbs = "";
  if (isLoading) {
    loadedBreadcrumbs = "Loading ...";
  } else if (hasLoaded) {
    loadedBreadcrumbs = (
      <Breadcrumbs>
        <Breadcrumbs.Item render={() => <Link to="/">Home</Link>} />
        <Breadcrumbs.Item
          render={() => <Link to="/admin">Administration</Link>}
        />
        <Breadcrumbs.Item render={() => <Link to="/admin/users">Users</Link>} />
        <Breadcrumbs.Item
          render={() => (
            <Link to={`/admin/users/${id}`}>
              User:{" "}
              {userData ? userData.last_name + ", " + userData.first_name : ""}
            </Link>
          )}
        />
        {accessId ? (
          <>
            <Breadcrumbs.Item
              render={() => (
                <Link to={`/admin/users/${id}/access/${accessId}`}>
                  Access:
                  {userAccessData.role
                    ? userAccessData.role.name +
                      " role for " +
                      userAccessData.client.name
                    : ""}
                </Link>
              )}
            />
            <Breadcrumbs.Item last render={() => "Edit"}></Breadcrumbs.Item>
          </>
        ) : (
          <Breadcrumbs.Item last render={() => `Add Access`} />
        )}
      </Breadcrumbs>
    );
  }

  const isVisible = selectedUser => {
    if (selectedUser.length !== 0)
      return (
        user.role === "Global Admin" ||
        selectedUser.role.name !== "Global Admin"
      );
    return true;
  };

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          <Flex.Item span={12}>{loadedBreadcrumbs}</Flex.Item>
          {isLoading ? (
            <Spinner />
          ) : hasLoaded ? (
            <Flex.Item span={12}>
              <Content>
                {accessId ? (
                  <h1>
                    Edit Access:{" "}
                    {userAccessData.role
                      ? userAccessData.role.name +
                        " role for " +
                        userAccessData.client.name
                      : ""}
                  </h1>
                ) : (
                  <h1>Add Access</h1>
                )}
              </Content>
              <div className={styles.base}>
                <div className={styles.detailsFlex}>
                  <h3>Access Details</h3>
                </div>
                <div className={styles.details}>
                  <Formik
                    initialValues={{
                      id: userAccessData.id,
                      user: id,
                      client: userAccessData.client
                        ? userAccessData.client.id
                        : 0,
                      access: userAccessData.role,
                      createdBy: userAccessData.created_by,
                      updatedBy: userAccessData.updated_by
                    }}
                    validationSchema={Yup.object().shape({
                      client: Yup.string()
                        .trim()
                        .required("Account is required")
                        .test(
                          "Unique Access",
                          (userAccessData.role
                            ? userAccessData.role.name
                            : "") +
                            " Access for this account is already present.",
                          function(client) {
                            if (accessId === undefined) {
                              //add access check
                              let url = `${userRoleApiUrl}/?user__id=${id}`;
                              if (client !== undefined) {
                                url += `&client__id=${client}`;
                              }
                              return new Promise((resolve, reject) => {
                                // This will check if user has already Organization Admin access for selected client/account's org
                                fetch(`${clientRpcUrl}/?id=${client}`)
                                  .then(handleResponse)
                                  .then(response => {
                                    const orgId =
                                      response.results[0].organization.id;
                                    fetch(`${userRoleRpcUrl}/?user__id=${id}`)
                                      .then(handleResponse)
                                      .then(response => {
                                        const orgIds = [];
                                        response.results.forEach(userRole => {
                                          if (
                                            userRole.role.name ===
                                            "Organization Admin"
                                          ) {
                                            orgIds.push(
                                              userRole.client.organization.id
                                            );
                                          }
                                        });
                                        if (!orgIds.includes(orgId)) {
                                          axios.get(url).then(res => {
                                            if (
                                              res.status === 204 ||
                                              accessId ===
                                                res?.data?.results[0]?.id
                                            ) {
                                              resolve(true);
                                            } else {
                                              resolve(false);
                                            }
                                          });
                                        } else {
                                          resolve(false);
                                        }
                                      });
                                  });
                              });
                            }
                            return true;
                          }
                        ),
                      access: Yup.string()
                        .required("Please select user Access level.")
                        .test(
                          "Unique Access",
                          (userAccessData.role
                            ? userAccessData.role.name !== "Organization Admin"
                              ? "Organization Admin"
                              : userAccessData.role.name
                            : "") +
                            " Access for this account is already present.",
                          function(access) {
                            if (access !== undefined) {
                              if (accessId === undefined) {
                                //add access check
                                let url = `${userRoleApiUrl}/?user__id=${id}`;
                                if (this.parent.client !== undefined) {
                                  url += `&client__id=${this.parent.client}`;
                                  return new Promise((resolve, reject) => {
                                    axios.get(url).then(res => {
                                      if (
                                        res.status === 204 ||
                                        accessId === res?.data?.results?.[0]?.id
                                      ) {
                                        resolve(true);
                                      }
                                      resolve(false);
                                    });
                                  });
                                } else {
                                  return true;
                                }
                              } else {
                                //update access check
                                return new Promise((resolve, reject) => {
                                  if (
                                    userAccessData.role.name ===
                                    "Organization Admin"
                                  ) {
                                    axios
                                      .get(
                                        `${userRoleApiUrl}/?user__id=${id}&client__id=${this.parent.client}&role__id=${access}`
                                      )
                                      .then(res => {
                                        if (
                                          res.status === 204 ||
                                          accessId === res?.data?.results?.[0]?.id
                                        ) {
                                          resolve(true);
                                        }
                                        resolve(false);
                                      });
                                  } else {
                                    // This will check if user has already Organization Admin access for selected client/account's org
                                    fetch(
                                      `${clientRpcUrl}/?id=${this.parent.client}`
                                    )
                                      .then(handleResponse)
                                      .then(response => {
                                        const orgId =
                                          response.results[0].organization.id;
                                        fetch(
                                          `${userRoleRpcUrl}/?user__id=${id}`
                                        )
                                          .then(handleResponse)
                                          .then(response => {
                                            const orgIds = [];
                                            response.results.forEach(
                                              userRole => {
                                                if (
                                                  userRole.role.name ===
                                                  "Organization Admin"
                                                ) {
                                                  orgIds.push(
                                                    userRole.client.organization
                                                      .id
                                                  );
                                                }
                                              }
                                            );
                                            if (!orgIds.includes(orgId)) {
                                              axios
                                                .get(
                                                  `${userRoleApiUrl}/?user__id=${id}&client__id=${this.parent.client}&role__id=${access}`
                                                )
                                                .then(res => {
                                                  if (
                                                    res.status === 204 ||
                                                    accessId ===
                                                      res?.data?.results?.[0]?.id
                                                  ) {
                                                    resolve(true);
                                                  } else {
                                                    resolve(false);
                                                  }
                                                });
                                            } else {
                                              resolve(false);
                                            }
                                          });
                                      });
                                  }
                                  return true;
                                });
                              }
                            }
                            return true;
                          }
                        )
                    })}
                    onSubmit={(values, { setSubmitting, resetForm }) => {
                      setSubmitting(true);
                      let data = {
                        user: `${userApiUrl}/${values.user}/`,
                        client: `${clientApiUrl}/${values.client}/`,
                        role: `${roleApiUrl}/${values.access}/`,
                        updated_by: user.username,
                        created_by: values.createdBy
                      };
                      let method = "";
                      let url = "";

                      if (accessId) {
                        data["id"] = accessId;
                        method = "put";
                        url = `${userRoleApiUrl}/${accessId}/`;
                      } else {
                        data["created_by"] = user.username;
                        method = "post";
                        url = `${userRoleApiUrl}/`;
                      }

                      addEditFetch({
                        method,
                        url,
                        data
                      });

                      setSubmitting(false);
                    }}
                  >
                    {props => {
                      const {
                        values,
                        errors,
                        handleChange,
                        handleBlur,
                        handleSubmit
                      } = props;

                      return (
                        <form onSubmit={handleSubmit}>
                          <div>
                            {accessId ? (
                              <>
                                <h4>Access ID</h4>
                                <span>{userAccessData.id}</span>
                              </>
                            ) : null}
                          </div>
                          <div>
                            <h4>User</h4>
                            <span>
                              {userData
                                ? userData.last_name +
                                  ", " +
                                  userData.first_name
                                : ""}
                            </span>
                          </div>
                          <div>
                            {accessId ? (
                              <>
                                <h4>Client</h4>
                                <span>
                                  {userAccessData.client
                                    ? userAccessData.client.name
                                    : ""}
                                </span>
                              </>
                            ) : (
                              <DropDownList
                                errorText={errors.client}
                                id="client"
                                label={
                                  <Label error={errors.client} htmlFor="client">
                                    <b>Account</b>
                                  </Label>
                                }
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.client}
                                width="25%"
                              >
                                <DropDownList.Item value="">
                                  Choose an account...
                                </DropDownList.Item>
                                {userClientsFilter.map(organization => (
                                  <DropDownList.Group
                                    key={organization.text}
                                    label={organization.text}
                                  >
                                    {organization.children.map(client => (
                                      <DropDownList.Item
                                        key={client.value}
                                        value={client.id}
                                      >
                                        {client.text}
                                      </DropDownList.Item>
                                    ))}
                                  </DropDownList.Group>
                                ))}
                              </DropDownList>
                            )}
                          </div>
                          <div>
                            <DropDownList
                              errorText={errors.access}
                              id="access"
                              name="access"
                              label={
                                <Label error={errors.access} htmlFor="access">
                                  <b>Access</b>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              width="25%"
                            >
                              <DropDownList.Item value="">
                                Choose an access...
                              </DropDownList.Item>
                              {rolesData.map((role, roleIndex) => {
                                return (
                                  <DropDownList.Item
                                    value={role.id}
                                    selected={
                                      accessId &&
                                      role.name === userAccessData.role.name
                                    }
                                  >
                                    {role.name}
                                  </DropDownList.Item>
                                );
                              })}
                            </DropDownList>
                          </div>
                          {accessId ? (
                            <>
                              <div>
                                <h4>Created By</h4>
                                <span>{userAccessData.created_by}</span>
                              </div>
                              <div>
                                <h4>Date Created</h4>
                                <span>
                                  {moment(userAccessData.created_at)
                                    .tz(user.preferences.timeZone)
                                    .format("L, LTS z")}
                                </span>
                              </div>
                              <div>
                                <h4>Edited By</h4>
                                <span>{userAccessData.updated_by}</span>
                              </div>
                              <div>
                                <h4>Date Updated</h4>
                                <span>
                                  {moment(userAccessData.updated_at)
                                    .tz(user.preferences.timeZone)
                                    .format("L, LTS z")}
                                </span>
                              </div>
                            </>
                          ) : null}

                          <ButtonList>
                            <Button
                              primary
                              type="submit"
                              disabled={!isVisible(userAccessData)}
                            >
                              {accessId ? "Save Changes" : "Save"}
                            </Button>
                            <Button
                              onClick={() => {
                                history.push(`/admin/users/${id}`);
                              }}
                            >
                              Cancel
                            </Button>
                          </ButtonList>
                        </form>
                      );
                    }}
                  </Formik>
                </div>
              </div>
            </Flex.Item>
          ) : null}
        </Flex>
      </Container>
    </Layout>
  );
};

export default AddEditUserAccess;
